import { DabsiApp } from '../types/App';
import { Org, OrgPhoneNumber } from '../types/Org';
import { api } from './api';

const API_URL = process.env.REACT_APP_API_URL;

export const createOrg = async (
  org: {
    name: string;
    wabaId: string;
    description: string;
  },
  adminEmail: string
): Promise<Org> => {
  return await api.post(
    `${API_URL}/orgs`,
    { org, adminEmail },
    'Failed to create organization'
  );
};

export const getOrgs = async (): Promise<Org[]> => {
  return await api.get(`${API_URL}/orgs`, 'Failed to get organizations');
};

export const getOrgsForUser = async (): Promise<Org[]> => {
  return await api.get(`${API_URL}/orgs/user`, 'Failed to get organizations');
};

export const getOrg = async (orgId: number): Promise<Org> => {
  return await api.get(
    `${API_URL}/orgs/${orgId}`,
    'Failed to get organization details'
  );
};

export const getOrgApps = async (orgId: number): Promise<DabsiApp[]> => {
  return await api.get(
    `${API_URL}/orgs/${orgId}/apps`,
    'Failed to get organization apps'
  );
};

/**
 * One row per (org, app, sidecar) the org has enabled. Surfaced by the
 * backend's GET /orgs/:orgId/sidecars endpoint and consumed by the
 * `useSidecarAccess` hook to gate org-conditional sidecar UI without
 * having to probe the gated routes first.
 *
 * Mirrors the backend's `OrgSidecar` interface in
 * `server/src/queries/orgSidecars.ts`.
 *
 * The backend's `getOrgSidecars` query maps DB columns (`app_id`,
 * `sidecar_key`) to camelCase (`appId`, `sidecarKey`) at the query
 * layer. So `GET /api/orgs/:orgId/sidecars` already returns camelCase
 * directly — the api wrapper's snakeToCamel layer is a no-op for
 * this endpoint.
 *
 * Until a shared types schema or generated OpenAPI client lands,
 * **changes here must be reflected in the backend definition**
 * (and vice versa); otherwise the on-the-wire shape silently drifts.
 */
export interface OrgSidecar {
  appId: string;
  sidecarKey: string;
  enabled: boolean;
  config: Record<string, unknown>;
}

export const getOrgSidecars = async (orgId: number): Promise<OrgSidecar[]> => {
  return await api.get(
    `${API_URL}/orgs/${orgId}/sidecars`,
    'Failed to get organization sidecars'
  );
};

export const getOrgPhoneNumbers = async (
  orgId: number
): Promise<OrgPhoneNumber[]> => {
  return await api.get(
    `${API_URL}/orgs/${orgId}/phone_numbers`,
    'Failed to get organization phone numbers'
  );
};

export const addOrgPhoneNumber = async (
  orgId: number,
  {
    label,
    phoneNumber,
    phoneNumberId,
  }: {
    label: string;
    phoneNumber: string;
    phoneNumberId: string;
  }
): Promise<OrgPhoneNumber> => {
  return await api.post(
    `${API_URL}/orgs/${orgId}/phone_numbers`,
    { label, phoneNumber, phoneNumberId },
    'Failed to add phone number to organization'
  );
};

export const getOptInLink = async ({
  orgId,
  phoneNumberId,
}: {
  orgId: number;
  phoneNumberId: string;
}): Promise<{ link: string }> => {
  return await api.get(
    `${API_URL}/orgs/${orgId}/phone_numbers/${phoneNumberId}/opt-in-link`,
    'Failed to generate opt-in link'
  );
};
